import { getCurrentEvent } from "core-integration/src/store/events/reducers";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import { hiDPI, size } from "polished";
import { useEffect } from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import styled from "styled-components";
// Hero images
import heroImg1x from "../img/home/hero.jpg";
import heroImg2x from "../img/home/hero@2x.jpg";
import AltiboxLogo from "../img/logos/altibox-logo.svg?react";
import NTLogo from "../img/logos/norsk-tipping.svg?react";
import ObosLogo from "../img/logos/obos-logo.svg?react";
import Tv2Logo from "../img/logos/tv2-logo.svg?react";
import {
  getHeroProviders,
  getHighlightsUrl,
  getScoutProviders,
  IHighlights,
  IProvider,
  IProviderWithTitle,
} from "../utils/contentFeeds";
import { getSite } from "../utils/sites";
import HeroProvider from "./HeroProvider";
import HighlightsProvider from "./highlights/HighlightsProvider";
import { Main, Wrapper } from "./Layout";
import Login from "./Login";
import ScoutProvider from "./scout/ScoutProvider";
import Status from "./status/Status";

const HomeHero = styled.div`
  background-repeat: no-repeat;
  background-position: 50% 0;
  background-image: url(${heroImg1x});

  ${hiDPI(2)} {
    background-image: url(${heroImg2x});
    background-size: cover;
  }

  margin: 0 ${({ theme }) => theme.space[3]};
  margin-bottom: 100px;
  padding-top: 270px;
  text-align: center;
  height: 0;
  position: relative;
  border-radius: 12px;
  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    border-radius: 12px;
  }
`;

const Tagline = styled.h3`
  display: inline-block;
  margin: 0 ${({ theme }) => theme.space[2]};
  padding: ${({ theme }) => theme.space[5]};
  background: ${({ theme }) => theme.colors.primary};
  font-family: ${({ theme }) => theme.fonts.impact};
  font-size: 20px;
  transform: translateY(-50%);
  line-height: 1;
  border-radius: 12px;

  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    font-size: 34px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    font-size: 52px;
  }
`;

const VideoEmbed = styled.div`
  display: flex;
  justify-content: center;
  margin: ${({ theme }) => `${theme.space[6]} ${theme.space[2]} 0`};

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    display: none;
  }
`;

const SponsorBarOuter = styled.div`
  background-color: #f2f9fa;
`;

const SponsorBarInner = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    max-width: 1220px;
    margin: auto;
  }
`;

const SponsorList = styled.ul`
  margin: 0;
  padding: ${({ theme }) => theme.space[2]};
  list-style-type: none;
  text-align: center;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    display: flex;
    justify-content: center;
    padding-top: 3rem;
    padding-bottom: 3rem;
  }
`;

const SponsorItem = styled.li`
  margin-bottom: ${({ theme }) => theme.space[3]};

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    flex: 0 0 27%;
    margin-bottom: 0;
    padding: 0 5rem;
    text-align: right;

    &:not(:first-child) {
      border-left: 2px solid #a5a5a5;
      text-align: left;
    }
  }
`;

const SponsorBox = styled.div`
  text-align: center;
  display: inline-block;
`;

const SponsorHeading = styled.h3`
  margin: 0 0 ${({ theme }) => theme.space[3]};
  font-size: 1.4rem;
  font-family: ${({ theme }) => theme.fonts.baseSemiBold};
  color: ${({ theme }) => theme.colors.black};

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    margin-bottom: 0.5rem;
  }
`;

const SponsorWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.space[2]};

  @media (min-width: ${({ theme }) => theme.breakpoints[1]}) {
    flex-direction: row;
    gap: ${({ theme }) => theme.space[8]};
  }
`;

const StyledAltiboxLogo = styled(AltiboxLogo)`
  ${size(37, 84)}

  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    ${size(55, 133)}
  }
`;

const StyledNTLogo = styled(NTLogo)`
  ${size(37, 140)}

  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    ${size(35, 210)}
  }
`;

const StyledObosLogo = styled(ObosLogo)`
  ${size(37, 84)}

  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    ${size(55, 133)}
  }
`;

const PresentedList = styled.ul`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  list-style-type: none;

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    margin-top: 2rem;
  }
`;

const PresentedItem = styled.li`
  padding: 0;
  line-height: 1;

  &:not(:last-child) {
    padding-right: ${({ theme }) => theme.space[2]};
  }
`;

const StyledTv2Logo = styled(Tv2Logo)`
  ${size(32, 41)}

  @media (min-width: ${({ theme }) => theme.breakpoints[4]}) {
    ${size(40, 53)}
    margin-top: -5px;
  }
`;

type Props = WithTranslation;

const Home = ({ t }: Props) => {
  const now = useSelector(getCurrentEvent);
  const player = useSelector(getPlayerData);
  const language = getSite() === "main_en" ? "en" : "nb";
  const hightlights = getHighlightsUrl();
  const scoutProviders = getScoutProviders(t);
  const heroProviders = getHeroProviders();

  const TrackFacebook = (): void => {
    const fbq = (window as any).fbq;
    if (fbq) {
      fbq("trackCustom", "FPLLanding");
    }
  };

  useEffect(() => {
    TrackFacebook();
  }, []);

  // Show the status page if have a team and the game has started
  if (now && player && player.entry) {
    return <Status />;
  }

  return (
    <>
      <Wrapper>
        <Main isWide={true}>
          <Login />
          <HomeHero>
            <Tagline>{t("home.title", "How to play Fantasy")}</Tagline>
          </HomeHero>
          {heroProviders[language].map((provider: IProvider) => (
            <HeroProvider
              key={provider.url}
              link={provider.link}
              url={provider.url}
              numberOfArticles={4}
            />
          ))}
          <VideoEmbed>
            <iframe
              width="560"
              height="315"
              src="https://youtube.com/embed/cLracM095a0?feature=shared"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            ></iframe>
          </VideoEmbed>
          <VideoEmbed>
            <iframe
              width="560"
              height="315"
              src="https://www.youtube.com/embed/ioBwHygiLIg?si=crHSUC54UKEGaBF6"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            ></iframe>
          </VideoEmbed>
          {language === "nb" &&
            scoutProviders.map((provider: IProviderWithTitle) => (
              <ScoutProvider
                key={provider.url}
                link={provider.link}
                url={provider.url}
                title={provider.title}
                numberOfArticles={4}
              />
            ))}
          {hightlights.map((highlight: IHighlights) => (
            <HighlightsProvider
              key={highlight.url}
              url={highlight.url}
              numberOfHighlights={4}
            />
          ))}
        </Main>
      </Wrapper>
      <SponsorBarOuter>
        <SponsorBarInner>
          <SponsorList>
            <SponsorItem>
              <SponsorBox>
                <SponsorHeading>
                  {t("home.mainPartner", "Main partner")}
                </SponsorHeading>
                <SponsorWrap>
                  <a
                    href="https://www.altibox.no/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <StyledAltiboxLogo />
                  </a>
                  <a
                    href="https://www.norsk-tipping.no/selskapet"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <StyledNTLogo />
                  </a>
                  <a
                    href="https://nye.obos.no/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <StyledObosLogo />
                  </a>
                </SponsorWrap>
              </SponsorBox>
            </SponsorItem>
            <SponsorItem>
              <SponsorBox>
                <SponsorHeading>
                  {t("home.poweredBy", "Powered by")}
                </SponsorHeading>
                <PresentedList>
                  <PresentedItem>
                    <a
                      href="https://www.tv2.no/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <StyledTv2Logo />
                    </a>
                  </PresentedItem>
                </PresentedList>
              </SponsorBox>
            </SponsorItem>
          </SponsorList>
        </SponsorBarInner>
      </SponsorBarOuter>
    </>
  );
};

export default withTranslation()(Home);
